/**
 * Participant Dashboard Types
 * 
 * TypeScript interfaces for dashboard data structures
 */

/**
 * Profile status enumeration matching backend
 */
export enum ProfileStatus {
  NEVER_FILLED = 'NEVER_FILLED',
  STALE = 'STALE',
  CURRENT = 'CURRENT',
}

/**
 * User profile data for display in Profile Status Card
 */
export interface ProfileData {
  status: ProfileStatus;
  hasProfile: boolean;
  isIncomplete: boolean;
  gender?: string;
  dateOfBirth?: string;
  ageRange?: string;
  educationLevel?: string;
  economicLevel?: string;
  city?: {
    name: string;
    state: {
      name: string;
      abbreviation: string;
    };
  };
  lastUpdatedAt?: string;
}

/**
 * Single survey item for Open Surveys Card
 */
export interface OpenSurveyItem {
  id: string;
  title: string;
  contractor: {
    name: string;
  };
}

/**
 * Open surveys data with total count
 */
export interface OpenSurveysData {
  total: number;
  surveys: OpenSurveyItem[];
}

/**
 * Single survey item for Completed Surveys Card
 */
export interface CompletedSurveyItem {
  id: string;
  title: string;
  submittedAt: string; // ISO format date string
  responseHash: string;
  publicAuditHash?: string;
}

/**
 * Completed surveys data with pagination
 */
export interface CompletedSurveysData {
  total: number;
  surveys: CompletedSurveyItem[];
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Complete dashboard data structure
 * Returned by GET /api/participant/dashboard
 */
export interface ActiveSummary {
  total: number;
  answeredCount: number;
}

export interface DashboardData {
  profile: ProfileData;
  openSurveys: OpenSurveysData;
  completedSurveys: CompletedSurveysData;
  activeSummary: ActiveSummary;
  currentCyclePoints: number | null;
}

/**
 * API response wrapper (optional, for consistent error handling)
 */
export interface ApiResponse<T> {
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
}
